import { forwardRef, useContext, useImperativeHandle, useState } from 'react';
import { Field, Panel } from '../../../../layouts';
import { EstimateContext } from '../context/EstimateContext';
import { TextBox, Validator } from 'devextreme-react';
import { RequiredRule, EmailRule, PatternRule } from 'devextreme-react/cjs/validator';
import { showErrorMessage, showSuccessMessage } from '../../../../utils/messages';
import { UpdateEstimateResult } from '../../../cfi/estimate/types/UpdateEstimateResult';
import { EstimateUpdateType } from '../../../cfi/estimate/types/UpdateEstimateParams';
import { useUpdateEstimate } from '../../../cfi/estimate/api/update-estimate';

interface CustomerInformationPanelProps {
    setEditMode: (value: boolean) => void;
}

interface CustomerInformationPanelRef {
    save: (e: any) => void;
}

function CustomerInformationPanelBase(
    { setEditMode }: CustomerInformationPanelProps,
    ref: React.Ref<CustomerInformationPanelRef>
) {

	const { order } = useContext(EstimateContext);

	const [phone, setPhone] = useState(order.details.customerPhone);
    const [email, setEmail] = useState(order.details.customerEmail);

	const updateEstimateCustomerInfo = useUpdateEstimate(
        {
            updateType: EstimateUpdateType.CustomerInfo,
            estimate: {
                ...order.details,
                activeARs: [],
                resolvedARs: [],
                items: [],
                quoteID: order.details.quoteID,
                customerPhone: phone,
				customerEmail: email
            }
        },
        (result: UpdateEstimateResult) => {
            if (result.isSuccess) {
				setEditMode(false);
                showSuccessMessage('Customer information has been updated');
            } else {
                showErrorMessage(result.errorMessage);
            }
        },
        (error) => showErrorMessage(error)
    );

	const save = (e: any) => updateEstimateCustomerInfo.mutate();

    useImperativeHandle(ref, () => ({
        save
    }));

	return (
		<>
			<Panel
				header={'Customer Information'}
				className={'field-label-35'}
			>
				<Field label={'Name'} >{order.details.customerName}</Field>
				<Field label={'Address'} >{order.details.customerAddress1}</Field>
				<Field label={''} visible={!!order.details.customerAddress2}>{order.details.customerAddress2}</Field>
				<Field label={''} >{order.details.customerCityZip}</Field>
				<Field label={'Phone'} allowEdit={true} editControl={<TextBox
							value={phone}
							className='required'
							mask='(000) 000-0000'
							useMaskedValue={true}
							onValueChanged={(e) => {
								setEditMode(true);
								setPhone(e.value);
							}}
						>
							<Validator validationGroup='customerInformationValidationGroup'>
								<RequiredRule message="Phone is required" />
							</Validator>
						</TextBox>}>
					{ phone }
				</Field>
				<Field label={'Alternate Phone'} >{order.details.customerAlternatePhone}</Field>
				<Field label={'Email'} allowEdit={true} editControl={<TextBox
							value={email}
							className='required'
							onValueChanged={(e) => {
								setEditMode(true);
								setEmail(e.value);
							}}
						>
							<Validator validationGroup='customerInformationValidationGroup'>
								<RequiredRule message="Email is required" />
								<EmailRule message="Invalid email format" />
							</Validator>
						</TextBox>}>
					<a href={'mailto:' + email}>{email}</a>
				</Field>
			</Panel>
		</>
	);
}

const CustomerInformationPanel = forwardRef<CustomerInformationPanelRef, CustomerInformationPanelProps>(CustomerInformationPanelBase);

CustomerInformationPanel.displayName = 'CustomerInformationPanel';

export default CustomerInformationPanel;